import React, { useEffect, useRef, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import JsBarcode from 'jsbarcode';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { numeroSeguro } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { escaparHtml } from '../utils/exportar';

// Etiqueta con código de barras (CODE128). Dos formas de imprimir:
//  - Imprimir: ventana del navegador (impresora normal / AirPrint / PC).
//  - Descargar imagen: PNG para imprimir desde la app de la etiquetadora
//    (ej. Phomemo M110 vía Print Master), ya que las etiquetadoras Bluetooth
//    no se pueden imprimir directo desde el navegador en iPad/iPhone.
export default function EtiquetaModal({ producto, onCerrar }) {
  const svgRef = useRef(null);
  const [copias, setCopias] = useState('1');
  const { mostrarToast } = useToast();
  const { negocio } = useAuth();
  const tienda = negocio?.nombre || 'Mi Negocio';

  useEffect(() => {
    if (svgRef.current) {
      JsBarcode(svgRef.current, String(producto.codigo), {
        format: 'CODE128',
        width: 2,
        height: 44,
        displayValue: true,
        fontSize: 13,
        margin: 4,
      });
    }
  }, [producto.codigo]);

  const imprimir = () => {
    const n = Math.min(100, Math.max(1, Math.trunc(numeroSeguro(copias, 1)) || 1));
    const svg = svgRef.current.outerHTML;
    const etiqueta = `
      <div class="etiqueta">
        <p class="tienda">${escaparHtml(tienda)}</p>
        <p class="nombre">${escaparHtml(producto.nombre)}${producto.talla ? ' · ' + escaparHtml(producto.talla) : ''}</p>
        ${svg}
        <p class="precio">${escaparHtml(simboloMoneda())} ${escaparHtml(producto.precio_venta)}</p>
      </div>`;
    const ventana = window.open('', '_blank', 'width=400,height=500');
    if (!ventana) {
      mostrarToast('El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes.', 'error', 6000);
      return;
    }
    ventana.document.write(`<!doctype html><html><head><title>Etiquetas ${escaparHtml(producto.codigo)}</title>
      <style>
        @page { size: 50mm 30mm; margin: 0; }
        body { margin: 0; font-family: Arial, sans-serif; }
        .etiqueta { width: 50mm; height: 30mm; box-sizing: border-box; padding: 1mm;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          text-align: center; page-break-after: always; overflow: hidden; }
        .tienda { margin: 0; font-size: 7pt; font-weight: bold; }
        .nombre { margin: 0; font-size: 7pt; max-width: 48mm; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis; }
        .precio { margin: 0; font-size: 10pt; font-weight: bold; }
        svg { max-width: 46mm; height: auto; }
      </style></head><body>${etiqueta.repeat(n)}
      <script>window.onload = function() { window.print(); }${'<'}/script></body></html>`);
    ventana.document.close();
  };

  // Genera la etiqueta como imagen PNG (para imprimir desde Print Master).
  const descargarImagen = () => {
    const W = 600, H = 360;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#000000'; ctx.textAlign = 'center';

    ctx.font = 'bold 26px Arial';
    ctx.fillText(tienda, W / 2, 46);

    ctx.font = '24px Arial';
    let linea2 = producto.nombre + (producto.talla ? ' · ' + producto.talla : '');
    if (ctx.measureText(linea2).width > W - 30) {
      while (linea2.length > 5 && ctx.measureText(linea2 + '…').width > W - 30) linea2 = linea2.slice(0, -1);
      linea2 += '…';
    }
    ctx.fillText(linea2, W / 2, 84);

    const bc = document.createElement('canvas');
    JsBarcode(bc, String(producto.codigo), { format: 'CODE128', width: 3, height: 120, displayValue: true, fontSize: 22, margin: 10 });
    const maxW = W - 60;
    const escala = Math.min(1, maxW / bc.width);
    const dw = bc.width * escala, dh = bc.height * escala;
    ctx.drawImage(bc, (W - dw) / 2, 108, dw, dh);

    ctx.font = 'bold 36px Arial';
    ctx.fillText(`${simboloMoneda()} ${producto.precio_venta}`, W / 2, H - 26);

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiqueta-${producto.codigo}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      mostrarToast('Etiqueta descargada 📷 Ábrela en Print Master para imprimir', 'exito', 6000);
    }, 'image/png');
  };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '400px', maxWidth: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--color-primario,#DB2777)', fontSize: '18px' }}>🏷️ Etiqueta</h2>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ border: '1px dashed #D1D5DB', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
          <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: 'bold' }}>{tienda}</p>
          <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#374151' }}>
            {producto.nombre}{producto.talla ? ` · ${producto.talla}` : ''}
          </p>
          <svg ref={svgRef} />
          <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: 'bold' }}>{simboloMoneda()} {producto.precio_venta}</p>
        </div>

        <button onClick={descargarImagen} style={{ width: '100%', padding: '12px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '8px' }}>
          📷 Descargar imagen (para etiquetadora)
        </button>
        <p style={{ margin: '0 0 14px', fontSize: '11px', color: '#9CA3AF' }}>
          Para la Phomemo M110: descarga y abre la imagen en la app <strong>Print Master</strong>.
        </p>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
          <label style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Copias:</label>
          <CampoNumero valor={copias} onChange={setCopias} decimales={0} min={1} minSiVacio max={100}
            maxMensaje="Como máximo 100 etiquetas por impresión"
            estilo={{ width: '70px', padding: '8px', borderRadius: '6px', fontSize: '14px' }} />
        </div>
        <button onClick={imprimir} style={{ width: '100%', padding: '10px', backgroundColor: 'white', color: 'var(--color-primario,#DB2777)', border: '2px solid var(--color-primario,#DB2777)', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
          🖨️ Imprimir en impresora normal
        </button>
        <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
          Tamaño de etiqueta: 50×30mm
        </p>
      </div>
    </div>
  );
}
